<!-- src/components/dashboard/WeeklyTillsCard.vue -->
<!-- CONTENT ONLY -->
<template>
  <div class="weekly-tills-card-content">
    <div v-if="missingVenue" class="grey-text text-darken-1" style="margin-top: 12px">
      Select a venue to view tills.
    </div>

    <div v-else-if="error" class="red-text" style="margin-top: 12px">
      {{ error }}
    </div>

    <div v-else style="margin-top: 12px">
      <div v-if="loading" class="progress">
        <div class="indeterminate"></div>
      </div>

      <div v-else class="responsive-table weekly-tills-table-wrap">
        <table class="striped">
          <thead>
            <tr>
              <th>Day</th>
              <th class="right-align">Actual Total</th>
              <th class="right-align">Z-Read Total</th>
              <th class="right-align">Variance</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="d in days" :key="d.key">
              <td>
                {{ d.label }}
              </td>

              <td class="right-align">
                {{ moneyGBP(d.actualTotal) }}
              </td>

              <td class="right-align">
                {{ moneyGBP(d.zReadTotal) }}
              </td>

              <td class="right-align">
                <span :class="varianceClass(d.variance)" style="font-weight: 700">
                  {{ signedMoneyGBP(d.variance) }}
                </span>
              </td>
            </tr>

            <tr class="grey lighten-4" style="font-weight: 700">
              <td>Week Total</td>

              <td class="right-align">
                {{ moneyGBP(weekActualTotal) }}
              </td>

              <td class="right-align">
                {{ moneyGBP(weekZReadTotal) }}
              </td>

              <td class="right-align">
                <span :class="varianceClass(weekVariance)" style="font-weight: 800">
                  {{ signedMoneyGBP(weekVariance) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="grey-text text-darken-1" style="margin-top: 10px">
          <span v-if="(apiRows?.length || 0) === 0">
            No till data found for this week.
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";

import { useVenueContext } from "@/scripts/useScripts/useVenueContext";

import { getTillsForDateRange } from "@/scripts/sqlApiHelper/tillsApi";

const { venueId, weekEnding } = useVenueContext();

const loading = ref(false);
const error = ref("");
const apiRows = ref([]);

const missingVenue = computed(() => {
  return !venueId?.value;
});

function num(v) {
  const n = Number(v);

  return Number.isFinite(n) ? n : 0;
}

function moneyGBP(n) {
  const v = Math.round(num(n) * 100) / 100;

  return v.toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function signedMoneyGBP(n) {
  const v = Math.round(num(n) * 100) / 100;

  const abs = Math.abs(v).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return v < 0 ? `-${abs}` : abs;
}

function varianceClass(v) {
  return num(v) < 0 ? "red-text" : "green-text";
}

function parseYmd(ymd) {
  const [y, m, d] = String(ymd)
    .split("-")
    .map((n) => Number(n));

  return new Date(Date.UTC(y, m - 1, d));
}

function toYmd(dateObj) {
  const y = dateObj.getUTCFullYear();

  const m = String(dateObj.getUTCMonth() + 1).padStart(2, "0");

  const d = String(dateObj.getUTCDate()).padStart(2, "0");

  return `${y}-${m}-${d}`;
}

function addDays(dateObj, n) {
  const d = new Date(dateObj.getTime());

  d.setUTCDate(d.getUTCDate() + n);

  return d;
}

const weekEndingISO = computed(() => {
  const raw = weekEnding?.value || "";

  if (!raw) {
    return "";
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }

  const d = new Date(raw);

  if (!Number.isNaN(d.getTime())) {
    return d.toISOString().slice(0, 10);
  }

  return String(raw).replace(/\//g, "-");
});

const weekDates = computed(() => {
  if (!weekEndingISO.value) {
    return [];
  }

  const we = parseYmd(weekEndingISO.value);

  const mon = addDays(we, -6);

  return Array.from({ length: 7 }, (_, i) => toYmd(addDays(mon, i)));
});

const weekLabel = computed(() => {
  if (!weekEndingISO.value) {
    return "";
  }

  const we = parseYmd(weekEndingISO.value);

  const mon = addDays(we, -6);

  const fmt = (d) =>
    d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
    });

  return `${fmt(mon)}–${fmt(we)}`;
});

function extractTradingDate(row) {
  const td = row?.trading_date ?? row?.tradingDate ?? row?.date ?? row?.trading_day;

  return td ? String(td).slice(0, 10) : "";
}

function extractLines(row) {
  if (!row || typeof row !== "object") {
    return [];
  }

  if (Array.isArray(row.lines)) {
    return row.lines;
  }

  if (Array.isArray(row.till_count_lines)) {
    return row.till_count_lines;
  }

  if (Array.isArray(row.tillCountLines)) {
    return row.tillCountLines;
  }

  return [];
}

const byDate = computed(() => {
  const seeded = {};

  for (const ymd of weekDates.value) {
    seeded[ymd] = {
      counted: 0,
      declared: 0,
    };
  }

  const rows = Array.isArray(apiRows.value) ? apiRows.value : [];

  for (const r of rows) {
    const ymd = extractTradingDate(r);

    if (!ymd || !seeded[ymd]) {
      continue;
    }

    const lines = extractLines(r);

    for (const ln of lines) {
      const type = String(ln?.line_type ?? ln?.lineType ?? "").toLowerCase();

      const amt = num(ln?.amount);

      if (type === "subtotal") {
        seeded[ymd].counted += amt;
      }

      if (type === "declared") {
        seeded[ymd].declared += amt;
      }
    }

    if (!lines.length) {
      const counted = num(r?.counted_total ?? r?.countedTotal);

      const declared = num(r?.declared_total ?? r?.declaredTotal);

      seeded[ymd].counted += counted;
      seeded[ymd].declared += declared;
    }
  }

  return seeded;
});

const days = computed(() => {
  const labels = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  return weekDates.value.map((ymd, i) => {
    const v = byDate.value?.[ymd] || {
      counted: 0,
      declared: 0,
    };

    const actualTotal = num(v.counted);

    const zReadTotal = num(v.declared);

    const variance = actualTotal - zReadTotal;

    return {
      key: ymd,
      label: labels[i],
      actualTotal,
      zReadTotal,
      variance,
    };
  });
});

const weekActualTotal = computed(() =>
  days.value.reduce((s, d) => s + num(d.actualTotal), 0)
);

const weekZReadTotal = computed(() =>
  days.value.reduce((s, d) => s + num(d.zReadTotal), 0)
);

const weekVariance = computed(() => weekActualTotal.value - weekZReadTotal.value);

async function load() {
  error.value = "";
  apiRows.value = [];

  if (!venueId?.value || !weekEndingISO.value || weekDates.value.length !== 7) {
    return;
  }

  loading.value = true;

  try {
    const res = await getTillsForDateRange({
      venue_id: venueId.value,
      date_from: weekDates.value[0],
      date_to: weekDates.value[6],
    });

    apiRows.value = Array.isArray(res?.rows) ? res.rows : Array.isArray(res) ? res : [];
  } catch (e) {
    error.value = e?.message ? String(e.message) : "Failed to load weekly tills.";
  } finally {
    loading.value = false;
  }
}

defineExpose({
  weekActualTotal,
  weekZReadTotal,
  weekVariance,
  weekLabel,
  signedMoneyGBP,
  varianceClass,
  moneyGBP,
  refresh: load,
});

onMounted(load);

watch(
  () => [venueId?.value, weekEndingISO.value],
  () => load()
);
</script>

<style scoped>
.weekly-tills-card-content {
  width: 100%;
}

@media (max-width: 600px) {
  .weekly-tills-table-wrap table {
    min-width: 620px;
  }
}
</style>
